import React, { useCallback, useMemo, useState } from "react";
import axiosInstance from "../../utils/axiosInstance";
import useCommanderSearch from "./useCommanderSearch";
import { normalizeCommanderOption } from "./commanderUtils";
import "./css/CommanderPicker.css";

const CommanderPicker = ({
  value,
  onChange,
  mode = "event", // event | nation | local
  scopeId = "",
  localCommanders = [],
  allianceNameById = {},
  preferAllianceId = "",
  limit = 120,
  disabled = false,
  searchPlaceholder = "Type commander name...",
  placeholder = "Select commander",
}) => {
  const [query, setQuery] = useState("");

  const normalizedLocalOptions = useMemo(() => {
    return (localCommanders || [])
      .map((commander) => normalizeCommanderOption(commander, allianceNameById))
      .filter(Boolean);
  }, [localCommanders, allianceNameById]);

  const fetchRemote = useCallback(async ({ query: nextQuery }) => {
    const q = String(nextQuery || "").trim();
    if (!scopeId) return [];

    if (mode === "event") {
      const params = new URLSearchParams();
      if (q) params.set("search", q);
      params.set("limit", String(limit));
      const response = await axiosInstance.get(`/events/${scopeId}/commanders?${params.toString()}`);
      return (response?.data?.commanders || [])
        .map((commander) => normalizeCommanderOption(commander, allianceNameById))
        .filter(Boolean);
    }

    if (mode === "nation") {
      const params = new URLSearchParams();
      params.set("name", q);
      params.set("page", "1");
      params.set("limit", String(limit));
      const response = await axiosInstance.get(`/nations/${scopeId}/commanders/search?${params.toString()}`);
      return (response?.data?.commanders || [])
        .map((commander) => normalizeCommanderOption(commander, allianceNameById))
        .filter(Boolean);
    }

    return [];
  }, [scopeId, mode, limit, allianceNameById]);

  const effectiveMode = mode === "local" ? "local" : "remote";

  const { options, isLoading } = useCommanderSearch({
    mode: effectiveMode,
    query,
    localOptions: normalizedLocalOptions,
    fetchRemote,
    enabled: !disabled && (effectiveMode === "local" || Boolean(scopeId)),
    debounceMs: 250,
  });

  const sortedOptions = useMemo(() => {
    return [...(options || [])].sort((a, b) => {
      const aPreferred = String(a?.allianceId || "") === String(preferAllianceId || "") ? 1 : 0;
      const bPreferred = String(b?.allianceId || "") === String(preferAllianceId || "") ? 1 : 0;
      if (aPreferred !== bPreferred) return bPreferred - aPreferred;
      return String(a?.commanderName || "").localeCompare(String(b?.commanderName || ""));
    });
  }, [options, preferAllianceId]);

  return (
    <div className="commander-picker">
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={searchPlaceholder}
        disabled={disabled}
      />
      <select
        value={value || ""}
        onChange={(e) => {
          const selectedValue = e.target.value;
          const selectedOption = sortedOptions.find((option) => String(option.value) === String(selectedValue)) || null;
          onChange?.(selectedValue, selectedOption);
        }}
        disabled={disabled}
      >
        <option value="">{isLoading ? "Searching..." : placeholder}</option>
        {sortedOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default CommanderPicker;
